import { SocketClient } from "cbcore-ts"
import { IF, UIColor, UIKeyValueSorter, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CBTableRowView } from "./Custom components/CB/CBTableRowView"
import { CellView } from "./Custom components/CellView"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHTables } from "./Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "./Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHAdminDetailsDialog } from "./RHAdminDetailsDialog"
import { RHCompany, RHUserProfilePrivate } from "./SocketClientFunctions"


// One tree row: a company (whose employees are its sub-rows) or a user without a company.
type RHAdminTreeRow = { company: RHCompany, user?: undefined } | { user: RHUserProfilePrivate, company?: undefined }

export class SystemAdministrationViewController extends UIViewController {
    
    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "Administration"
    }).addedAsSubviewToView(this.view)
    
    readonly explanationView: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "System administration panel for managing users and companies."
    })
    
    readonly dataView = new RHDataView<RHAdminTreeRow>()
        .configuredWithObject({
            isSearchAvailable: YES,
            isTreeView: YES,
            style: {
                borderColor: UIColor.lightGreyColor.stringValue,
                borderWidth: "1px"
            },
            descriptors: [
                {
                    title: "Type",
                    keyPath: "",
                    allowSorting: YES,
                    dataType: UIKeyValueSorter.dataType.string,
                    updateCell: (cellView: CellView, dataItem: RHAdminTreeRow) => {
                        const isCompany = !!dataItem.company
                        cellView.titleLabel!.text = isCompany ? "Company" : "User"
                        cellView.titleLabel!.style.color = isCompany ? UIColor.blueColor.stringValue : UIColor.greyColor.stringValue
                    }
                },
                {
                    title: "Nimi / e-posti aadress",
                    keyPath: "",
                    allowSorting: YES,
                    dataType: UIKeyValueSorter.dataType.string,
                    updateCell: (cellView: CellView, dataItem: RHAdminTreeRow) => {
                        cellView.titleLabel!.text = dataItem.company ? dataItem.company.name : dataItem.user.email
                    }
                },
                {
                    title: "ID",
                    keyPath: "_id",
                    allowSorting: YES,
                    dataType: UIKeyValueSorter.dataType.string
                }
            ]
        }).addedAsSubviewToView(this.view)
    
    readonly bottomView = new UIView().addedAsSubviewToView(this.view).configuredWithObject({
        style: {
            backgroundColor: UIColor.lightGreyColor.stringValue,
            borderTop: `1px solid ${UIColor.greyColor.stringValue}`
        }
    })
    
    readonly statusLabel = new UITextView().addedAsSubviewToView(this.bottomView).configuredWithObject({
        text: "No selection",
        style: { fontSize: "14px" }
    })
    
    private readonly detailsDialog: RHAdminDetailsDialog = new RHAdminDetailsDialog("AdminDetailsDialog")
    
    constructor(view: UIView) {
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: UIColor.whiteColor
        })
        
        this.dataView.tableView.controlEventTargetAccumulator.PointerUpInside = (sender: UIView) => {
            let dataItem = this.dataView.highlightedDataItem
            
            if (!dataItem && sender instanceof CBTableRowView && sender.data) {
                dataItem = sender.data as RHAdminTreeRow
            }
            
            if (dataItem) {
                const identityText = dataItem.company ? dataItem.company.name : dataItem.user.email
                this.statusLabel.text = "Showing details for: " + RHTextSanitizer.escapedHTML(identityText ?? "")
                this.detailsDialog.configureForDataItem(dataItem)
                this.detailsDialog.showInRootView(YES)
            }
        }
    }
    
    static override readonly routeComponentName = "sysadmin"
    static override readonly ParameterIdentifierName = {}
    
    override async handleRoute(route: UIRoute) {
        super.handleRoute(route)
        
        
        const requestResult = await SocketClient.RetrieveListOfUsersAndCompanies()
        if (requestResult.errorResult) {
            console.log(requestResult.errorResult)
            route.routeByRemovingComponentNamed(SystemAdministrationViewController.routeComponentName).apply()
            return
        }
        const data = requestResult.result
        console.log(data)
        this.dataView.setTreeData(
            [
                ...data.companies.map((company): RHAdminTreeRow => ({ company })),
                ...data.unemployedUsers.map((user): RHAdminTreeRow => ({ user }))
            ],
            // @ts-ignore
            "company.companyEmployees"
        )
        
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        bounds
            .rectangleWithHeight(padding * 2).assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding * 0.25, this.explanationView).assignedAsFrameOfView(this.explanationView)
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight).assignedAsFrameOfView(this.dataView)
            .rectangleForNextRow(padding, padding * 3).assignedAsFrameOfView(this.bottomView)
        
        this.bottomView.bounds.rectangleWithInset(padding)
            .distributeViewsEquallyAlongWidth([this.statusLabel], 0)
    }
    
}
